/**
 * VariableSetEditor - Table view with Create/Edit/Delete for project-level variables
 *
 * Uses VariableFormDialog (popup) for create/edit — same visual pattern as TasksTable.
 * Click on row → opens edit dialog. Checkbox for selection. Actions menu per row.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { VariableFormDialog } from '@modules/product/components/VariableFormDialog';
import { useVariableMutation } from '@modules/product/hooks/useVariableMutation';
import { cloneSnapshot } from '@modules/firebase-core/utils/variableSnapshot';
import type { VariableFormData, TaskRef } from '@modules/product/components/VariableFormDialog';
import { PRODUCT_VARIABLES } from '@test-kit/selectors/variable-privacy';

/** Variable type labels for display */
const TYPE_LABELS: Record<string, string> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Yes/No',
  date: 'Date',
  enum: 'Single Choice',
  'enum[]': 'Multiple Choice',
};

/** Re-export for backward compatibility */
export type EditorVariable = VariableFormData;
export type { TaskRef };

export type VariableChangeHandler = (variables: Record<string, EditorVariable>, reviewed: Record<string, EditorVariable>) => void | Promise<void>;

interface VariableSetEditorProps {
  variables: Record<string, EditorVariable>;
  onChange: VariableChangeHandler;
  tasks?: TaskRef[];
  filterTaskId?: string | null;
  disabled?: boolean;
  /** Optional: enable manual version snapshots */
  onSaveVersion?: (description: string) => void | Promise<void>;
  /** Optional: show "Promote to Project" button (strategy context) */
  onPromoteToProject?: () => void | Promise<void>;
  /** Current version number (for display) */
  currentVersion?: number;
}

export const VariableSetEditor: React.FC<VariableSetEditorProps> = ({
  variables,
  onChange,
  tasks = [],
  filterTaskId = null,
  disabled: externallyDisabled = false,
  onSaveVersion,
  onPromoteToProject,
  currentVersion,
}) => {
  const { saving, error, run } = useVariableMutation();
  const disabled = externallyDisabled || saving;
  const reviewedDraft = React.useRef<Record<string, EditorVariable>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState<VariableFormData | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuVariableId, setMenuVariableId] = useState<string | null>(null);
  const [analyticsMenuAnchor, setAnalyticsMenuAnchor] = useState<null | HTMLElement>(null);
  const [requiredMenuAnchor, setRequiredMenuAnchor] = useState<null | HTMLElement>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  const handleToggleDescription = useCallback((id: string) => {
    setExpandedDescriptions(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);

  const sortedVariables = useMemo(() => {
    let vars = Object.values(variables);
    if (filterTaskId) {
      vars = vars.filter(v => !v.taskIds?.length || v.taskIds.includes(filterTaskId));
    }
    return vars;
  }, [variables, filterTaskId]);

  const existingIds = useMemo(() => Object.keys(variables), [variables]);

  const handleCreate = useCallback(() => {
    if (disabled) return;
    reviewedDraft.current = cloneSnapshot(variables);
    setEditingVariable(null);
    setDialogOpen(true);
  }, [variables, disabled]);

  const handleEdit = useCallback((variable: EditorVariable) => {
    if (disabled) return;
    reviewedDraft.current = cloneSnapshot(variables);
    setEditingVariable(reviewedDraft.current[variable.id]);
    setDialogOpen(true);
  }, [variables, disabled]);

  const handleDelete = useCallback((id: string) => {
    const newVars = { ...variables };
    delete newVars[id];
    void run(() => onChange(newVars, variables), () => setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; }));
  }, [variables, onChange, run]);

  const handleDeleteSelected = useCallback(() => {
    const newVars = { ...variables };
    selectedIds.forEach(id => delete newVars[id]);
    void run(() => onChange(newVars, variables), () => setSelectedIds(new Set()));
  }, [variables, onChange, selectedIds, run]);

  // Bulk writes touch ONLY selected ids — unselected rows keep their values,
  // including the meaningful `excludeFromAnalytics: undefined` (review required).
  const handleBulkAnalytics = useCallback((exclude: boolean) => {
    const newVars = { ...variables };
    selectedIds.forEach(id => {
      if (newVars[id]) newVars[id] = { ...newVars[id], excludeFromAnalytics: exclude };
    });
    setAnalyticsMenuAnchor(null);
    void run(() => onChange(newVars, variables));
  }, [variables, onChange, selectedIds, run]);

  const handleBulkRequired = useCallback((required: boolean) => {
    const newVars = { ...variables };
    selectedIds.forEach(id => {
      if (newVars[id]) newVars[id] = { ...newVars[id], required };
    });
    setRequiredMenuAnchor(null);
    void run(() => onChange(newVars, variables));
  }, [variables, onChange, selectedIds, run]);

  const handleDuplicate = useCallback((id: string) => {
    const original = variables[id];
    if (!original) return;
    let copyId = `${original.id}_copy`;
    let suffix = 2;
    while (Object.prototype.hasOwnProperty.call(variables, copyId)) copyId = `${original.id}_copy_${suffix++}`;
    const newVars = { ...variables };
    newVars[copyId] = { ...original, id: copyId, name: `${original.name} (Copy)` };
    void run(() => onChange(newVars, variables));
  }, [variables, onChange, run]);

  const handleOpenMenu = useCallback((e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuVariableId(id);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuVariableId(null);
  }, []);

  const handleSave = useCallback((data: VariableFormData) => {
    const baseline = reviewedDraft.current;
    const newVars = { ...baseline };
    if (editingVariable && editingVariable.id !== data.id) delete newVars[editingVariable.id];
    Object.defineProperty(newVars, data.id, { value: data, enumerable: true, writable: true, configurable: true });
    void run(() => onChange(newVars, baseline), () => {
      setDialogOpen(false);
      setEditingVariable(null);
    });
  }, [onChange, editingVariable, run]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === sortedVariables.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedVariables.map(v => v.id)));
    }
  }, [selectedIds.size, sortedVariables]);

  const allSelected = sortedVariables.length > 0 && selectedIds.size === sortedVariables.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < sortedVariables.length;

  return (
    <Box data-testid={PRODUCT_VARIABLES.editor} aria-busy={saving}>
      {error && !dialogOpen && <Typography role="alert" color="error" sx={{ mb: 1 }}>{error}</Typography>}
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">
            {sortedVariables.length} variable{sortedVariables.length !== 1 ? 's' : ''}
          </Typography>
          {selectedIds.size > 0 && (
            <>
              <Chip
                data-testid={PRODUCT_VARIABLES.selectionCount}
                label={selectedIds.size}
                size="small"
                color="primary"
                sx={{ fontWeight: 'medium' }}
              />
              <Button
                data-testid={PRODUCT_VARIABLES.bulkAnalyticsMenu}
                size="small"
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setAnalyticsMenuAnchor(e.currentTarget)}
                disabled={disabled}
                sx={{ textTransform: 'none' }}
              >
                Analytics
              </Button>
              <Button
                data-testid={PRODUCT_VARIABLES.bulkRequiredMenu}
                size="small"
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setRequiredMenuAnchor(e.currentTarget)}
                disabled={disabled}
                sx={{ textTransform: 'none' }}
              >
                Required
              </Button>
              <Tooltip title="Delete selected">
                <IconButton
                  data-testid={PRODUCT_VARIABLES.bulkDelete}
                  onClick={handleDeleteSelected}
                  disabled={disabled}
                  color="error"
                  size="small"
                  aria-label="Delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear selection">
                <IconButton
                  data-testid={PRODUCT_VARIABLES.clearSelection}
                  onClick={() => setSelectedIds(new Set())}
                  size="small"
                  aria-label="Clear selection"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onSaveVersion && (
            <Button
              startIcon={<SaveIcon />}
              size="small"
              onClick={() => {
                const desc = prompt('Version description:');
                if (desc) void run(() => onSaveVersion(desc));
              }}
              disabled={disabled || sortedVariables.length === 0}
              variant="outlined"
              color="secondary"
            >
              Save Version{currentVersion ? ` (v${currentVersion})` : ''}
            </Button>
          )}
          {onPromoteToProject && (
            <Button
              startIcon={<PublishIcon />}
              size="small"
              onClick={() => void run(onPromoteToProject)}
              disabled={disabled || sortedVariables.length === 0}
              variant="outlined"
              color="success"
            >
              Promote to Project
            </Button>
          )}
          <Button
            data-testid={PRODUCT_VARIABLES.addButton}
            startIcon={<AddIcon />}
            size="small"
            onClick={handleCreate}
            disabled={disabled}
            variant="outlined"
          >
            Add New Variable
          </Button>
        </Box>
      </Box>

      {/* Empty state */}
      {sortedVariables.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No variables defined. Click "Add New Variable" to specify what data to collect from users.
          </Typography>
        </Box>
      )}

      {/* Table */}
      {sortedVariables.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    data-testid={PRODUCT_VARIABLES.selectAll}
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>Task Scope</TableCell>
                <TableCell>Analytics</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVariables.map((variable) => {
                const isSelected = selectedIds.has(variable.id);
                const taskNames = variable.taskIds?.length
                  ? variable.taskIds.map(tid => tasks.find(t => t.id === tid)?.name ?? tid)
                  : null;

                return (
                  <TableRow
                    key={variable.id}
                    data-testid={PRODUCT_VARIABLES.row(variable.id)}
                    hover
                    selected={isSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleSelect(variable.id)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                    </TableCell>
                    <TableCell onClick={() => handleEdit(variable)}>
                      <Typography variant="body2" fontWeight={500}>
                        {variable.name || <em>Unnamed</em>}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {variable.id}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={() => handleEdit(variable)}>
                      <Chip
                        label={TYPE_LABELS[variable.type] ?? variable.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell onClick={() => handleEdit(variable)}>
                      {variable.required ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell onClick={() => handleEdit(variable)}>
                      {taskNames ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 150 }}>
                          {taskNames.map((name, idx) => (
                            <Chip key={idx} label={name} size="small" />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Global</Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={() => handleEdit(variable)}>
                      {variable.excludeFromAnalytics === undefined ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="This variable has no analytics decision yet: choose whether its answers are collected into analytics or excluded (privacy). Resolve opens the variable editor.">
                            <Chip
                              data-testid={PRODUCT_VARIABLES.status(variable.id)}
                              label="Review required"
                              size="small"
                              color="warning"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                          {!disabled && (
                            <Button
                              size="small"
                              variant="text"
                              data-testid={PRODUCT_VARIABLES.resolve(variable.id)}
                              onClick={(e) => { e.stopPropagation(); handleEdit(variable); }}
                              sx={{ minWidth: 'auto', px: 0.5, fontSize: '0.7rem', textTransform: 'none' }}
                            >
                              Resolve
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Chip
                          data-testid={PRODUCT_VARIABLES.status(variable.id)}
                          label={variable.excludeFromAnalytics === true ? 'Not collected' : 'Collected'}
                          size="small"
                          color={variable.excludeFromAnalytics === true ? 'default' : 'success'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell onClick={() => handleEdit(variable)}>
                      {(variable as any).insightSource ? (
                        <Chip
                          label="From Insights"
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f3e5f5', color: '#7b1fa2' }}
                        />
                      ) : (variable as any).tags?.includes('scraped') ? (
                        <Chip
                          label="From Scraping"
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                        />
                      ) : (
                        <Chip
                          label="Manual"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={(e) => {
                        // Long insight-derived descriptions clamp to 2 lines; a click
                        // expands in place instead of opening the editor.
                        if (variable.description && variable.description.length > 120) {
                          e.stopPropagation();
                          handleToggleDescription(variable.id);
                          return;
                        }
                        handleEdit(variable);
                      }}
                      sx={{ maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {variable.description ? (
                        <Typography
                          variant="body2"
                          sx={expandedDescriptions.has(variable.id) ? {} : {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {variable.description}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, variable.id)}
                        disabled={disabled}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bulk analytics decision menu */}
      <Menu
        anchorEl={analyticsMenuAnchor}
        open={Boolean(analyticsMenuAnchor)}
        onClose={() => setAnalyticsMenuAnchor(null)}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
          Set for {selectedIds.size} selected
        </Typography>
        <MenuItem
          data-testid={PRODUCT_VARIABLES.bulkAnalyticsCollect}
          onClick={() => handleBulkAnalytics(false)}
        >
          <ListItemText>Collect for analytics</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid={PRODUCT_VARIABLES.bulkAnalyticsExclude}
          onClick={() => handleBulkAnalytics(true)}
        >
          <ListItemText>Don't collect (privacy)</ListItemText>
        </MenuItem>
      </Menu>

      {/* Bulk required menu */}
      <Menu
        anchorEl={requiredMenuAnchor}
        open={Boolean(requiredMenuAnchor)}
        onClose={() => setRequiredMenuAnchor(null)}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
          Set for {selectedIds.size} selected
        </Typography>
        <MenuItem
          data-testid={PRODUCT_VARIABLES.bulkRequiredYes}
          onClick={() => handleBulkRequired(true)}
        >
          <ListItemText>Required: Yes</ListItemText>
        </MenuItem>
        <MenuItem
          data-testid={PRODUCT_VARIABLES.bulkRequiredNo}
          onClick={() => handleBulkRequired(false)}
        >
          <ListItemText>Required: No</ListItemText>
        </MenuItem>
      </Menu>

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          if (menuVariableId) handleEdit(variables[menuVariableId]);
          handleCloseMenu();
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuVariableId) handleDuplicate(menuVariableId);
          handleCloseMenu();
        }}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuVariableId) handleDelete(menuVariableId);
          handleCloseMenu();
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit dialog */}
      <VariableFormDialog
        open={dialogOpen}
        onClose={() => { if (!saving) { setDialogOpen(false); setEditingVariable(null); } }}
        isSaving={saving}
        saveError={error}
        onSave={handleSave}
        initialData={editingVariable}
        tasks={tasks}
        existingIds={editingVariable ? existingIds.filter(id => id !== editingVariable.id) : existingIds}
        defaultTaskId={filterTaskId ?? undefined}
      />
    </Box>
  );
};
